import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { api, AdminMe, ApiError, PublicConfig } from "../lib/api";

// Top-level shell for the /admin/* routes. Reads the current organiser
// identity once on mount and renders role-appropriate nav tabs:
//   - admin     → Operators · Sessions
//   - operator  → Sessions
//   - moderator → Assigned Sessions
// All roles get an identity badge so it's always visible what authority
// the current tab is acting under.
//
// /me failures fall back to a graceful empty state with a sign-in
// prompt — usually means the OAuth cookie expired and the page is
// running on stale state. Refreshing through Cloudflare/oauth2-proxy
// brings the cookie back via the OAuth login flow.
export default function AdminShell() {
  const [me, setMe] = useState<AdminMe | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Auth mode drives the 401 recovery copy below. We fetch /api/v1/config
  // in parallel with /api/v1/admin/me so the unauthenticated path doesn't
  // pay a sequential round-trip before telling the user where to go.
  // ``null`` = still loading; render the safe cluster-mode copy meanwhile,
  // which is also what's been shown since the OAuth-walled deploy shipped.
  const [authMode, setAuthMode] = useState<PublicConfig["auth_mode"] | null>(
    null,
  );
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [meRes, cfgRes] = await Promise.allSettled([
        api.adminMe(),
        api.config(),
      ]);
      if (cancelled) return;
      if (cfgRes.status === "fulfilled") setAuthMode(cfgRes.value.auth_mode);
      if (meRes.status === "fulfilled") {
        setMe(meRes.value);
      } else {
        const e = meRes.reason;
        setErr(
          e instanceof ApiError
            ? `${e.status}: ${e.message}`
            : "could not load identity",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (err) {
    return (
      <main>
        <h1>Sign-in required</h1>
        <p className="error" role="alert">
          {err}
        </p>
        {authMode === "magic_link" ? (
          <p>
            Your organiser session has expired or you're not signed in
            yet. <Link to="/login">Sign in with a one-tap email link</Link>
            {" "}to continue.
          </p>
        ) : (
          <p>
            Your organiser session may have expired. Try a hard refresh
            (Ctrl+Shift+R) — Cloudflare will redirect you through OAuth and
            land you back here.
          </p>
        )}
      </main>
    );
  }

  if (me === null) {
    return (
      <main>
        <p className="meta">Loading…</p>
      </main>
    );
  }

  const isAdmin = me.role === "admin";
  const isOperator = me.role === "operator";
  const isModerator = me.role === "moderator";

  return (
    <>
      <header
        style={{
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          padding: "0.75rem 1rem",
          borderBottom: "1px solid var(--border)",
          background: "var(--surface)",
          flexWrap: "wrap",
          marginBottom: "1rem",
          // Sticky so the role badge + nav tabs stay visible while
          // scrolling the long queue / session-list pages. Backdrop-blur
          // keeps the bar readable over scrolled content; the solid
          // background fallback covers browsers without backdrop-filter.
          position: "sticky",
          top: 0,
          zIndex: 20,
          backdropFilter: "saturate(140%) blur(6px)",
          WebkitBackdropFilter: "saturate(140%) blur(6px)",
        }}
      >
        <Link
          to="/admin"
          style={{
            fontWeight: 700,
            textDecoration: "none",
            color: "var(--fg)",
          }}
        >
          AskTogether
        </Link>
        <nav
          aria-label="Primary"
          style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}
        >
          {isAdmin && (
            <ShellTab to="/admin/operators" label="Operators" />
          )}
          <ShellTab
            to="/admin/sessions"
            label={
              isModerator ? "Assigned sessions"
                : isOperator ? "My sessions"
                : "Sessions"
            }
          />
        </nav>
        <span
          aria-label={`Signed in as ${me.email} (${me.role})`}
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.35rem",
            padding: "0.2rem 0.65rem",
            borderRadius: "999px",
            background: "var(--surface-elevated)",
            border: "1px solid var(--border)",
            color: "var(--fg-muted)",
            fontSize: "0.8rem",
          }}
        >
          <span aria-hidden="true">👤</span>
          {me.email}
          <strong style={{ color: "var(--fg)" }}>· {me.role}</strong>
        </span>
      </header>
      <Outlet context={me} key={location.pathname} />
    </>
  );
}

function ShellTab({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      end={to === "/admin/sessions"}
      style={({ isActive }) => ({
        padding: "0.35rem 0.75rem",
        borderRadius: "6px",
        textDecoration: "none",
        color: isActive ? "var(--accent-fg)" : "var(--fg)",
        background: isActive ? "var(--accent)" : "transparent",
        fontWeight: 600,
        fontSize: "0.9rem",
      })}
    >
      {label}
    </NavLink>
  );
}
